import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GraduationCap, Hourglass, CheckCircle2, Video, Lock } from 'lucide-react';
import { goPro } from '../lib/checkout';
import { FeedbackRequestModal } from './feedback/FeedbackRequestModal';

/* Dashboard → Exam History: every completed mock test + where its teacher review stands.
   ✅ Reviewed by teacher · ⏳ Submitted for review · 👩‍🏫 Apply for feedback · ▶ Watch your video feedback */
function FeedbackCell({ h, isPro, onApply }) {
  if (h.feedback_status === 'delivered' || h.teacher_reviewed) {
    return (
      <div className="flex items-center gap-1.5 flex-wrap justify-center">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${h.teacher_feedback_unread ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-800'}`} data-testid={`history-reviewed-${h.id}`}>
          <CheckCircle2 size={11} /> Reviewed by teacher{h.feedback_scope_label ? ` · ${h.feedback_scope_label}` : ''}{h.teacher_feedback_unread ? ' · new' : ''}
        </span>
        {h.feedback_open_label && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800" data-testid={`history-pending-${h.id}`}><Hourglass size={11} /> {h.feedback_open_label} submitted</span>}
        {h.feedback_loom_url ? (
          <a href={h.feedback_loom_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-emerald-700 text-white text-[11px] font-bold px-2.5 py-0.5 hover:bg-emerald-800" data-testid={`history-watch-${h.id}`}><Video size={11} /> Watch your video feedback</a>
        ) : (
          <Link to={`/exams/${h.exam_id}/results/${h.id}?from=review`} className="inline-flex items-center gap-1 rounded-full bg-emerald-700 text-white text-[11px] font-bold px-2.5 py-0.5 hover:bg-emerald-800" data-testid={`history-watch-${h.id}`}><GraduationCap size={11} /> Read your feedback</Link>
        )}
      </div>
    );
  }
  if (h.feedback_status === 'pending' || h.feedback_status === 'in_review') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800" data-testid={`history-pending-${h.id}`}><Hourglass size={11} /> Submitted for review{h.feedback_scope_label ? ` · ${h.feedback_scope_label}` : ''}</span>;
  }
  return (
    <button type="button" onClick={() => onApply(h)} className={`inline-flex items-center gap-1 rounded-full text-[11px] font-bold px-2.5 py-1 transition ${isPro ? 'bg-teal-700 text-white hover:bg-teal-800' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`history-apply-${h.id}`}>
      {isPro ? <GraduationCap size={11} /> : <Lock size={11} />} Apply for feedback{!isPro && ' (Pro)'}
    </button>
  );
}

export default function ExamHistoryTable({ rows, isPro, onChanged }) {
  const navigate = useNavigate();
  const [applying, setApplying] = useState(null);
  const [showAll, setShowAll] = useState(false);
  if (!rows.length) return null;
  const onApply = (h) => (isPro ? setApplying(h) : goPro(navigate));
  const fmtDate = (h) => (h.completed_at ? new Date(h.completed_at).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '—');
  const MOBILE_LIMIT = 3;
  const mobileRows = showAll ? rows : rows.slice(0, MOBILE_LIMIT);
  return (
    <div data-testid="exam-history">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <h2 className="font-[Manrope] font-bold text-slate-900">Exam History</h2>
        <p className="text-xs text-slate-500">{rows.length} completed test{rows.length === 1 ? '' : 's'}<span className="hidden sm:inline"> · a teacher can check any of them</span></p>
      </div>
      {/* Phones: one card per test (the wide table needs sideways scrolling); latest three first */}
      <div className="md:hidden space-y-2" data-testid="exam-history-cards">
        {mobileRows.map((h) => (
          <div key={h.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3.5" data-testid={`history-card-${h.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-slate-900 text-sm truncate">{h.exam_name}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">{h.target_skill || 'Full test'} · {fmtDate(h)}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-[Manrope] text-xl font-extrabold text-blue-700 leading-none">{h.overall_score || '—'}</p>
                <p className="text-[9.5px] uppercase tracking-wider text-slate-400 mt-0.5">Overall</p>
              </div>
            </div>
            <div className="mt-2.5 grid grid-cols-4 gap-1.5">
              {[['L', h.listening_score], ['R', h.reading_score], ['W', h.writing_score], ['S', h.speaking_score]].map(([k, v]) => (
                <div key={k} className="rounded-lg bg-slate-50 py-1 text-center"><div className="text-[10px] font-semibold text-slate-400">{k}</div><div className="text-sm font-bold text-slate-800">{v || '—'}</div></div>
              ))}
            </div>
            <div className="mt-2.5 flex items-center justify-between gap-2 flex-wrap">
              <FeedbackCell h={h} isPro={isPro} onApply={onApply} />
              <Link to={`/exams/${h.exam_id}/results/${h.id}`} className="text-blue-600 text-xs font-semibold hover:underline ml-auto" data-testid={`history-view-mobile-${h.id}`}>View results →</Link>
            </div>
          </div>
        ))}
        {rows.length > MOBILE_LIMIT && (
          <button type="button" onClick={() => setShowAll((s) => !s)} className="w-full rounded-2xl border border-dashed border-slate-200 bg-white py-2.5 text-xs font-semibold text-slate-600 hover:border-slate-400 transition" data-testid="exam-history-toggle">
            {showAll ? 'Show fewer' : `Show all ${rows.length} tests`}
          </button>
        )}
      </div>
      <div className="hidden md:block bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Exam</th>
                <th className="px-3 py-3 text-center font-medium text-slate-500">Skill</th>
                <th className="px-3 py-3 text-center font-medium text-slate-500">L</th>
                <th className="px-3 py-3 text-center font-medium text-slate-500">R</th>
                <th className="px-3 py-3 text-center font-medium text-slate-500">W</th>
                <th className="px-3 py-3 text-center font-medium text-slate-500">S</th>
                <th className="px-3 py-3 text-center font-medium text-slate-500">Overall</th>
                <th className="px-3 py-3 text-center font-medium text-slate-500">Date</th>
                <th className="px-3 py-3 text-center font-medium text-slate-500">Teacher feedback</th>
                <th className="px-3 py-3 text-center font-medium text-slate-500"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {rows.map((h) => (
                <tr key={h.id} className="hover:bg-blue-50/30" data-testid={`history-row-${h.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{h.exam_name}</td>
                  <td className="px-3 py-3 text-center"><span className="px-2 py-0.5 bg-slate-100 rounded text-xs">{h.target_skill || 'Full'}</span></td>
                  <td className="px-3 py-3 text-center text-slate-600">{h.listening_score || '—'}</td>
                  <td className="px-3 py-3 text-center text-slate-600">{h.reading_score || '—'}</td>
                  <td className="px-3 py-3 text-center text-slate-600">{h.writing_score || '—'}</td>
                  <td className="px-3 py-3 text-center text-slate-600">{h.speaking_score || '—'}</td>
                  <td className="px-3 py-3 text-center font-bold text-blue-700">{h.overall_score || '—'}</td>
                  <td className="px-3 py-3 text-center text-xs text-slate-400 whitespace-nowrap">{h.completed_at ? new Date(h.completed_at).toLocaleDateString() : '—'}</td>
                  <td className="px-3 py-3 text-center"><FeedbackCell h={h} isPro={isPro} onApply={onApply} /></td>
                  <td className="px-3 py-3 text-center"><Link to={`/exams/${h.exam_id}/results/${h.id}`} className="text-blue-600 text-xs font-medium hover:underline" data-testid={`history-view-${h.id}`}>View</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {applying && <FeedbackRequestModal attemptId={applying.id} examName={applying.exam_name} onClose={() => setApplying(null)} onDone={() => { setApplying(null); onChanged?.(); }} />}
    </div>
  );
}
